import type { ButtonHTMLAttributes, Ref } from 'react';
import './Button.css';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /*
    primary: the one main action in a row. secondary: an ordinary action. ghost: a quiet action that
    should not compete (Reorder, Clear). danger: a destructive action (white with a red edge; the
    confirm step owns the filled red).
  */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** md is the control height (36px); sm (28px) sits inside rows and dense toolbars. */
  size?: 'md' | 'sm';
  loading?: boolean;
  fullWidth?: boolean;
  // React 19 passes `ref` as an ordinary prop to function components — no forwardRef needed —
  // but the prop still has to appear in this type and be applied to the underlying <button> for
  // a caller to reach the real DOM node (e.g. a drop zone that needs `contains()` on it).
  ref?: Ref<HTMLButtonElement>;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  className = '',
  ref,
  ...props
}: ButtonProps) => {
  const classes = [
    'btn',
    `btn--${variant}`,
    size === 'sm' && 'btn--sm',
    fullWidth && 'btn--full-width',
    loading && 'btn--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
};
